/* Feb-2026 iter — IT Management panel.
 *
 * Scope-gated one-stop for network, secrets, cron reports, logs, and
 * the ITservices@ mailbox. Only admins carrying the `it_management`
 * (or grand-fathered `pro_writer`) scope can render this — routes are
 * gated server-side too.
 *
 * Deliberately read-heavy: mutating actions live in the existing
 * panels (Keys, Admin Users, Payments). IT sees enough to diagnose
 * without holding write-access to the underlying secrets. Retention
 * cron has a "Run now" button because it's the one action that lives
 * only in this panel.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, ShieldCheck, ShieldAlert,
  Terminal, Wifi, KeyRound, Server, FileText, Play, Loader2,
  Cpu, MemoryStick, HardDrive, Users, Save, Eye,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

function fmtTime(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso || ''; }
}

function fmtBytes(n) {
  if (!Number.isFinite(n) || n < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function fmtDuration(sec) {
  if (!Number.isFinite(sec)) return '—';
  const s = Math.floor(sec);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

function Meter({ percent, label, sub }) {
  const p = Math.max(0, Math.min(100, Number(percent) || 0));
  const tone = p >= 90 ? 'bg-rose-500' : p >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="rounded border border-border bg-background/70 p-3">
      <div className="flex items-center justify-between text-[11.5px]">
        <span className="font-semibold">{label}</span>
        <span className="font-mono text-[11px]">{p.toFixed(1)}%</span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded bg-muted">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${p}%` }} />
      </div>
      {sub && <div className="mt-1 text-[10.5px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Dot({ ok, configured = true }) {
  return (
    <span
      aria-hidden
      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${configured === false ? 'bg-muted-foreground/50' : ok ? 'bg-emerald-500' : 'bg-rose-500'}`}
    />
  );
}

function StatusPill({ ok, configured = true }) {
  if (configured === false) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold uppercase text-muted-foreground"
            data-testid="it-service-not-configured">
        Not configured
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider ${ok ? 'bg-emerald-500/15 text-emerald-600' : 'bg-rose-500/15 text-rose-600'}`}>
      {ok ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
      {ok ? 'Healthy' : 'Issue'}
    </span>
  );
}

// ─────────── Sections

function NetworkSection({ data, onRefresh, busy }) {
  const services = data?.services || [];
  return (
    <Card className="p-4" data-testid="it-network-health">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-[13.5px] font-semibold">
            <Wifi className="h-4 w-4" /> Network & Integration Health
          </h3>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            Reader · Email · Stripe · Alerts webhook — probed live every render.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={busy}
          className="h-7 gap-1 text-[11px]" data-testid="it-network-refresh">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </Button>
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {services.map((s, i) => (
          <div key={s.name || i} data-testid={`it-service-${(s.name || '').replace(/\s+/g, '-').toLowerCase()}`}
            className="flex items-start gap-3 rounded border border-border bg-background/70 p-3">
            <Dot ok={!!s.ok} configured={s.configured} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[12.5px] font-semibold">{s.name}</span>
                <StatusPill ok={!!s.ok} configured={s.configured} />
              </div>
              <p className="mt-0.5 text-[11px] text-muted-foreground break-words">
                {s.message}
                {s.base_url ? <> · <span className="font-mono text-[10.5px]">{s.base_url}</span></> : null}
                {s.mode ? <> · mode=<span className="font-mono text-[10.5px]">{s.mode}</span></> : null}
                {s.channel ? <> · via <span className="font-mono text-[10.5px]">{s.channel}</span></> : null}
              </p>
            </div>
          </div>
        ))}
        {services.length === 0 && (
          <div className="text-[11.5px] text-muted-foreground">No services returned.</div>
        )}
      </div>
      <div className="mt-2 text-[10.5px] text-muted-foreground">
        Checked {fmtTime(data?.checked_at)} · {data?.healthy}/{data?.total} healthy
      </div>
    </Card>
  );
}

function SecretsSection({ data, onRefresh, busy }) {
  const groups = data?.groups || [];
  return (
    <Card className="p-4" data-testid="it-secrets-audit">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-[13.5px] font-semibold">
            <KeyRound className="h-4 w-4" /> Secrets Audit
          </h3>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            Redacted inventory — never shows raw values. Rotate from{' '}
            <a href="/admin/operations" className="underline decoration-dotted">Admin → Keys</a>.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={busy}
          className="h-7 gap-1 text-[11px]" data-testid="it-secrets-refresh">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </Button>
      </div>
      <div className="mt-2 text-[11px] text-muted-foreground">
        Coverage: <span className="font-semibold text-foreground">{data?.present ?? 0} / {data?.total ?? 0}</span> keys present ({data?.coverage_pct ?? 0}%).
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {groups.map((g) => (
          <div key={g.heading} className="rounded border border-border bg-background/70 p-3"
            data-testid={`it-secret-group-${g.heading.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
            <h4 className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
              {g.heading}
            </h4>
            <ul className="mt-1 space-y-1">
              {(g.keys || []).map((k) => (
                <li key={k.key} className="flex items-center justify-between gap-2 text-[11.5px]">
                  <span className="truncate font-mono">{k.key}</span>
                  <span className="shrink-0 inline-flex items-center gap-1">
                    <Dot ok={!!k.present} />
                    {k.present
                      ? <span className="text-[10.5px] font-medium text-success">configured</span>
                      : <span className="text-[10.5px] uppercase tracking-wider text-rose-600">missing</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}

function CronSection() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const load = () => {
    setBusy(true);
    http.get('/admin/it/cron-reports', { params: { limit: 50 } })
      .then((r) => setRows(r.data?.runs || []))
      .catch(() => setRows([]))
      .finally(() => setBusy(false));
  };
  useEffect(load, []);
  const runRetention = async () => {
    setRunBusy(true);
    try {
      const r = await http.post('/admin/it/retention/run');
      toast.success(`Archived ${r.data?.archived ?? 0} thread(s) older than ${r.data?.days_threshold ?? 90} day(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retention run failed.');
    } finally { setRunBusy(false); }
  };
  return (
    <Card className="p-4" data-testid="it-cron-reports">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-[13.5px] font-semibold">
            <Activity className="h-4 w-4" /> Cron Reports
          </h3>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            Last 50 runs from every registered cron. Reconciliation orphans are highlighted amber
            when JEs remain unhealed after the backfill sweep. Retention prunes old inbox threads.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={runRetention} disabled={runBusy}
            className="h-7 gap-1 text-[11px]" data-testid="it-cron-retention-run">
            {runBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
            Run retention now
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={busy}
            className="h-7 gap-1 text-[11px]" data-testid="it-cron-refresh">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Refresh
          </Button>
        </div>
      </div>
      <div className="mt-3 max-h-72 overflow-y-auto rounded border border-border">
        <table className="w-full text-[11.5px]">
          <thead className="sticky top-0 bg-panel-header/60 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="p-2 text-left">Cron</th>
              <th className="p-2 text-left">When</th>
              <th className="p-2 text-left">Result</th>
              <th className="p-2 text-right">Duration</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} className="p-3 text-muted-foreground">
                No cron runs recorded yet. Crons will start writing here as they tick.
              </td></tr>
            )}
            {rows.map((r, i) => {
              const isRecon = r.name === 'reconciliation-backfill';
              const remaining = Number(r.remaining_missing || 0);
              const posted = (
                (r.ledger_entries?.posted || 0) +
                (r.payables?.posted || 0) +
                (r.cost_events?.posted || 0) +
                (r.payment_transactions?.posted || 0)
              );
              return (
                <tr key={r.id || i} className={`border-t border-border ${isRecon && remaining > 0 ? 'bg-amber-500/5' : ''}`}
                  data-testid={isRecon ? `it-cron-run-recon-${i}` : undefined}>
                  <td className="p-2 font-mono">{r.name}</td>
                  <td className="p-2">{fmtTime(r.at)}</td>
                  <td className="p-2">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                      r.ok === false ? 'bg-rose-500/15 text-rose-600'
                      : isRecon && remaining > 0 ? 'bg-amber-500/20 text-amber-700'
                      : 'bg-emerald-500/15 text-emerald-600'}`}>
                      {r.ok === false ? 'fail'
                        : isRecon && remaining > 0 ? `orphans: ${remaining}`
                        : isRecon && posted > 0 ? `healed: ${posted}`
                        : 'ok'}
                    </span>
                    {isRecon && (
                      <span className="ml-1.5 text-[10px] text-muted-foreground">
                        {`led:${r.ledger_entries?.posted || 0} pay:${r.payables?.posted || 0} ce:${r.cost_events?.posted || 0} pt:${r.payment_transactions?.posted || 0}`}
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-right font-mono text-[10.5px]">{r.duration_ms ? `${r.duration_ms} ms` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function LogsSection() {
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => {
    setBusy(true);
    http.get('/admin/it/log-reports', { params: { tail: 200 } })
      .then((r) => setLogs(r.data?.logs || []))
      .catch(() => setLogs([]))
      .finally(() => setBusy(false));
  };
  useEffect(load, []);
  return (
    <Card className="p-4" data-testid="it-log-reports">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-[13.5px] font-semibold">
            <FileText className="h-4 w-4" /> Log Reports
          </h3>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            Tail of the backend supervisor logs (last 200 lines each).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
          className="h-7 gap-1 text-[11px]" data-testid="it-logs-refresh">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </Button>
      </div>
      <div className="mt-3 space-y-3">
        {logs.map((l) => (
          <div key={l.path} data-testid={`it-log-${l.path.split('/').pop()}`}>
            <div className="flex items-center gap-2 text-[11px]">
              <Terminal className="h-3 w-3 text-muted-foreground" />
              <span className="font-mono">{l.path}</span>
              <span className="text-muted-foreground">· {l.line_count ?? 0} lines</span>
            </div>
            <pre className="mt-1 max-h-64 overflow-y-auto whitespace-pre-wrap break-all rounded border border-border bg-black/60 p-2 text-[10.5px] leading-relaxed text-emerald-200">
{(l.lines || []).slice(-100).join('\n') || '(empty)'}
            </pre>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ServerStressSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    setBusy(true);
    http.get('/admin/it/server-stress')
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load server stress.'))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);
  return (
    <Card className="p-4" data-testid="it-server-stress">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-[13.5px] font-semibold">
            <Activity className="h-4 w-4" /> Server Stress
          </h3>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            Live CPU / memory / disk / DB pressure. Auto-refreshes every 15 s. Reads only.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
          className="h-7 gap-1 text-[11px]" data-testid="it-stress-refresh">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </Button>
      </div>
      {!data ? (
        <div className="mt-3 text-[11.5px] text-muted-foreground">
          {busy ? 'Probing…' : 'No data yet.'}
        </div>
      ) : (
        <>
          <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-4">
            <Meter percent={data.cpu?.percent}
              label={<span className="inline-flex items-center gap-1"><Cpu className="h-3.5 w-3.5" /> CPU</span>}
              sub={`${data.cpu?.count || 1} cores · load ${(data.cpu?.load_avg_1_5_15 || []).map((n) => (n ?? 0).toFixed(2)).join(' / ')}`} />
            <Meter percent={data.memory?.percent}
              label={<span className="inline-flex items-center gap-1"><MemoryStick className="h-3.5 w-3.5" /> Memory</span>}
              sub={`${fmtBytes(data.memory?.used)} / ${fmtBytes(data.memory?.total)} used`} />
            <Meter percent={data.swap?.percent}
              label={<span className="inline-flex items-center gap-1"><MemoryStick className="h-3.5 w-3.5" /> Swap</span>}
              sub={`${fmtBytes(data.swap?.used)} / ${fmtBytes(data.swap?.total)}`} />
            <Meter percent={data.disk?.percent}
              label={<span className="inline-flex items-center gap-1"><HardDrive className="h-3.5 w-3.5" /> Disk (/)</span>}
              sub={`${fmtBytes(data.disk?.used)} used · ${fmtBytes(data.disk?.free)} free`} />
          </div>
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            <div className="rounded border border-border bg-background/70 p-3 text-[11.5px]">
              <div className="font-semibold">Backend process</div>
              <div className="mt-1 space-y-0.5 font-mono text-[11px] text-muted-foreground">
                <div>pid={data.process?.pid} · uptime {fmtDuration(data.process?.uptime_sec)}</div>
                <div>rss={fmtBytes(data.process?.rss)} · threads={data.process?.threads}
                  {data.process?.open_fds != null && <> · fds={data.process.open_fds}</>}
                </div>
              </div>
            </div>
            <div className="rounded border border-border bg-background/70 p-3 text-[11.5px]" data-testid="it-stress-db">
              <div className="flex items-center justify-between">
                <span className="font-semibold">MongoDB</span>
                <Dot ok={!!data.database?.ok} />
              </div>
              {data.database?.ok ? (
                <div className="mt-1 space-y-0.5 font-mono text-[11px] text-muted-foreground">
                  <div>conns {data.database.connections} · avail {data.database.connections_available}</div>
                  <div>uptime {fmtDuration(data.database.uptime_sec)} · ops in {data.database.opcounters?.insert ?? 0} · q {data.database.opcounters?.query ?? 0}</div>
                </div>
              ) : (
                <div className="mt-1 text-[11px] text-rose-600">{data.database?.error || 'Unavailable'}</div>
              )}
            </div>
          </div>
          <div className="mt-2 text-[10.5px] text-muted-foreground">
            Checked {fmtTime(data.checked_at)}
          </div>
        </>
      )}
    </Card>
  );
}

function SeatsSection() {
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);   // { scope, value }
  const [saving, setSaving] = useState(false);
  const load = () => {
    setBusy(true);
    http.get('/admin/it/seat-audit')
      .then((r) => setRows(r.data?.scopes || {}))
      .catch(() => setRows({}))
      .finally(() => setBusy(false));
  };
  useEffect(load, []);
  const save = async (scope) => {
    if (!editing || editing.scope !== scope) return;
    const limit = parseInt(editing.value, 10);
    if (!Number.isFinite(limit) || limit < 1 || limit > 100) {
      toast.error('Cap must be between 1 and 100'); return;
    }
    setSaving(true);
    try {
      await http.put(`/admin/it/seats/${scope}`, { limit });
      toast.success(`Cap for '${scope}' set to ${limit}`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update cap.');
    } finally { setSaving(false); }
  };
  const entries = Object.entries(rows);
  return (
    <Card className="p-4" data-testid="it-seat-audit">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-[13.5px] font-semibold">
            <Users className="h-4 w-4" /> Panel Seats
          </h3>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            Six seats each by default — assign / revoke on{' '}
            <a href="/admin" className="underline decoration-dotted">Admin → Admin Users</a>.
            IT admins can raise or lower a cap below. Read-only admins see the roster only.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
          className="h-7 gap-1 text-[11px]" data-testid="it-seats-refresh">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </Button>
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {entries.length === 0 && (
          <div className="text-[11.5px] text-muted-foreground">No seat-limited scopes configured.</div>
        )}
        {entries.map(([scope, info]) => {
          const pct = info.limit > 0 ? Math.round((info.used / info.limit) * 100) : 0;
          const isEditing = editing?.scope === scope;
          return (
            <div key={scope} className="rounded border border-border bg-background/70 p-3"
              data-testid={`it-seats-${scope}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[12px] font-semibold">{scope}</span>
                <span className="text-[11px] text-muted-foreground" data-testid={`it-seats-${scope}-count`}>
                  {info.used} / {info.limit}
                </span>
              </div>
              <div className="mt-1 h-1 overflow-hidden rounded bg-muted">
                <div className={`h-full ${pct >= 100 ? 'bg-rose-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'} transition-all`}
                  style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(info.holders || []).length === 0 && (
                  <span className="text-[10.5px] text-muted-foreground">No holders yet.</span>
                )}
                {(info.holders || []).map((h) => (
                  <span key={h} className="rounded bg-panel-header/60 px-1.5 py-0.5 text-[10.5px] font-mono"
                    data-testid={`it-seats-${scope}-holder-${h}`}>{h}</span>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-1">
                {isEditing ? (
                  <>
                    <Input type="number" min={1} max={100} value={editing.value}
                      onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                      className="h-7 w-20 text-[11px]"
                      data-testid={`it-seats-${scope}-input`} />
                    <Button size="sm" variant="outline" onClick={() => save(scope)} disabled={saving}
                      className="h-7 gap-1 px-2 text-[11px]"
                      data-testid={`it-seats-${scope}-save`}>
                      {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)} disabled={saving}
                      className="h-7 px-2 text-[11px]">Cancel</Button>
                  </>
                ) : (
                  <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-[11px]"
                    onClick={() => setEditing({ scope, value: String(info.limit) })}
                    data-testid={`it-seats-${scope}-edit`}>
                    Edit cap
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function ItMailboxSection() {  const [data, setData] = useState({ threads: [], unread: 0 });
  const [busy, setBusy] = useState(false);
  const load = () => {
    setBusy(true);
    http.get('/admin/it/inbox', { params: { status: 'open', limit: 50 } })
      .then((r) => setData(r.data || { threads: [], unread: 0 }))
      .catch(() => setData({ threads: [], unread: 0 }))
      .finally(() => setBusy(false));
  };
  useEffect(load, []);
  return (
    <Card className="p-4" data-testid="it-mailbox">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-[13.5px] font-semibold">
            <Server className="h-4 w-4" /> IT Mailbox
            {data.unread > 0 && (
              <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground"
                data-testid="it-mailbox-unread">
                {data.unread} unread
              </span>
            )}
          </h3>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            Threads addressed to <span className="font-mono">ITservices@crownnrose.com</span> —
            visible only to admins with the <span className="font-mono">it_management</span> scope.
            Open one to reply / archive from the main{' '}
            <a href="/admin" className="underline decoration-dotted">Administrator Inbox</a>{' '}
            (filter by folder <span className="font-mono">it</span>).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
          className="h-7 gap-1 text-[11px]" data-testid="it-mailbox-refresh">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </Button>
      </div>
      <div className="mt-3 max-h-64 overflow-y-auto rounded border border-border">
        {(data.threads || []).length === 0 ? (
          <div className="p-4 text-[11.5px] text-muted-foreground">
            No IT threads yet. Once the Cloudflare Worker routes{' '}
            <span className="font-mono">ITservices@crownnrose.com</span> into the inbound
            webhook, mail will land here automatically (folder <span className="font-mono">it</span>).
          </div>
        ) : (
          <ul>
            {(data.threads || []).map((t) => (
              <li key={t.id} className="border-b border-border p-2 text-[11.5px]" data-testid={`it-thread-${t.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="truncate font-semibold">{t.peer_name || t.peer_email}</div>
                  <div className="shrink-0 text-[10.5px] text-muted-foreground">{fmtTime(t.last_message_at)}</div>
                </div>
                <div className="truncate text-[11px] text-foreground/80">{t.subject || '(no subject)'}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

// ─────────── Root

export default function ItManagementPanel() {
  const [net, setNet] = useState(null);
  const [secrets, setSecrets] = useState(null);
  const [netBusy, setNetBusy] = useState(false);
  const [secBusy, setSecBusy] = useState(false);

  const loadNet = () => {
    setNetBusy(true);
    http.get('/admin/it/network-health')
      .then((r) => setNet(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load network health.'))
      .finally(() => setNetBusy(false));
  };
  const loadSecrets = () => {
    setSecBusy(true);
    http.get('/admin/it/secrets-audit')
      .then((r) => setSecrets(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load secrets audit.'))
      .finally(() => setSecBusy(false));
  };
  useEffect(() => { loadNet(); loadSecrets(); }, []);

  return (
    <div className="min-h-0 space-y-4" data-testid="it-management-panel">
      <div>
        <h2 className="flex items-center gap-2 text-[15px] font-semibold">
          <ShieldCheck className="h-4 w-4" /> IT Management
        </h2>
        <p className="mt-0.5 text-[11.5px] text-muted-foreground">
          Scope-isolated oversight of network health, secrets, cron activity, log tails, and
          the ITservices@ mailbox. Access is gated by the <span className="font-mono">it_management</span>{' '}
          admin scope — no data flows into the finance / users / operations groups.
        </p>
      </div>
      <NetworkSection data={net} onRefresh={loadNet} busy={netBusy} />
      <ServerStressSection />
      <SeatsSection />
      <SecretsSection data={secrets} onRefresh={loadSecrets} busy={secBusy} />
      <CronSection />
      <LogsSection />
      <ItMailboxSection />
    </div>
  );
}
